import animation, { fadeInUp } from '@helpers/animation';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import ArrowUrl from '@img/icons/arrow.svg?url';
import ChevronDownUrl from '@img/icons/icon-chevron-down.svg?url';
import { Typography } from '@stories/Elements/Typography/Typography.styles';
import { Grid } from '@stories/Settings/Grids/Grid.styles';
import styled, { css } from 'styled-components';
import { ProductListHeaderProps } from './ProductListHeader';

const Container = styled(Grid.Default)<Pick<ProductListHeaderProps, 'background' | 'removeMaxWidth'>>`
  ${({ removeMaxWidth }) =>
    removeMaxWidth &&
    css`
      max-width: unset;
    `};

  ${({ background }) => {
    switch (background) {
      default:
      case 'white': {
        return css`
          background-color: ${brand.white};
        `;
      }

      case 'grey': {
        return css`
          background-color: ${brand.grey[95]};
        `;
      }
    }
  }}
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 30px 20px 20px;
  grid-column: span 12;

  @media ${until(Device.TabletLarge)} {
    &[data-align='left'] {
      text-align: left;
    }
  }

  @media ${from(Device.TabletLarge)} {
    padding: 30px 40px 50px 40px;
  }

  &[data-nopadding-bottom='true'] {
    padding-bottom: 0px;
  }
`;

const Logo = styled.div<{ inView: boolean }>`
  display: inline-block;
  margin-bottom: 20px;

  ${({ inView }) => (inView !== undefined ? fadeInUp(inView, 25, 500, 0, animation.easeInOutCubic) : null)};

  img {
    max-height: 34px;
    width: 100%;

    @media ${from(Device.Tablet)} {
      max-height: 61px;
    }
  }
`;

const Content = styled.p<{ inView?: boolean }>`
  ${Typography.PStyle};
  max-width: 1250px;

  a {
    color: ${brand.red.base};

    &:visited {
      color: ${brand.red.base};
    }
  }

  ${({ inView }) => (inView !== undefined ? fadeInUp(inView, 25, 500, 250, animation.easeInOutCubic) : null)};
`;

const Title = styled.h1<{ inView: boolean }>`
  ${Typography.H5Style};
  margin-bottom: ${fluid(20, 32)};
  margin-top: 14px;
  max-width: 1250px;

  @media ${until(Device.TabletLarge)} {
    width: 100%;
  }

  em {
    font-style: normal;
    color: ${brand.red.base};
  }

  img {
    max-width: 45px;
    align-self: center;
    margin-right: 15px;
  }

  ${({ inView }) => (inView !== undefined ? fadeInUp(inView, 25, 500, 150, animation.easeInOutCubic) : null)};
`;

const SubTitle = styled.div<{ inView: boolean }>`
  ${fonts.helvetica['93-black-extended']};
  font-size: ${fluid(14, 18)};
  margin-bottom: ${fluid(20, 32)};
  text-transform: uppercase;
  width: 100%;
`;

const Back = styled.a`
  ${fonts.helvetica['55-roman']};
  font-size: 12px;
  color: ${brand.nearlyBlack};
  margin-bottom: 10px;
  position: relative;
  display: inline-block;
  margin-right: auto;
  text-transform: uppercase;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 5px;
  align-items: center;

  &:before {
    content: '';
    width: 16px;
    height: 8px;
    background-image: url(${ArrowUrl});
    background-position: 50%;
    background-size: contain;
    background-repeat: no-repeat;
    position: relative;
    display: block;
    transform: rotate(180deg);
  }
`;

const ReadMoreButton = styled.button.attrs({ type: 'button' })`
  ${ButtonReset};
  ${fonts.helvetica['53-extended']};
  color: ${brand.nearlyBlack};
  margin: 10px 0;
  position: relative;
  text-transform: uppercase;
  padding-right: 5px;
  cursor: pointer;

  &:after {
    content: '';
    width: 16px;
    height: 8px;
    background-image: url(${ChevronDownUrl});
    background-position: 50%;
    background-size: contain;
    background-repeat: no-repeat;
    position: absolute;
    top: 50%;
    right: -15px;
    transform: translateY(-50%);
  }
`;

const SearchTerms = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: ${fluid(20, 32)};
  max-width: 100%;

  @media ${until(Device.TabletLarge)} {
    width: 100%;
    flex-wrap: nowrap;
    overflow: auto;
  }
`;

const SearchTerm = styled.a`
  ${ButtonReset};
  padding: 8px;
  border: 1px solid ${brand.black};
  color: ${brand.black};
  border-radius: 4px;
  font-size: ${fluid(14, 16)};
  line-height: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  text-transform: capitalize;

  &:hover {
    background: ${brand.grey['90']};
  }

  svg {
    width: 16px;
    height: 16px;
    top: -2px;
    position: relative;
  }
`;

const SearchHelp = styled.div`
  padding: ${fluid(24, 40)} 0px;

  ul {
    margin-bottom: ${fluid(20, 32)};

    @media ${until(Device.TabletLarge)} {
      text-align: left;
      justify-content: flex-start;
    }
  }
`;

export const ProductListHeaderStyles = {
  Container,
  Header,
  Logo,
  Content,
  Title,
  SubTitle,
  Back,
  ReadMoreButton,
  SearchTerms,
  SearchTerm,
  SearchHelp,
};
